import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  Box,
  DataTable,
  Banner,
  Button,
  Icon,
  Link,
  Collapsible,
  ProgressBar,
  Thumbnail,
  Modal,
  Divider,
  Popover,
  ActionList,
} from '@shopify/polaris';
import { LockIcon, ExternalIcon, QuestionCircleIcon, CheckCircleIcon, XCircleIcon, RefreshIcon, ChevronDownIcon, ChevronUpIcon, StoreIcon, CheckIcon, MenuHorizontalIcon, AlertTriangleIcon } from '@shopify/polaris-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { TierBadge } from '../components/TierBadge';
import { ScoreCard } from '../components/ScoreCard';
import { TrendChart } from '../components/TrendChart';
import { DistributionHistogram } from '../components/DistributionHistogram';

import { AppEmbedStatusCard } from '../components/AppEmbedStatusCard';
import { EnableEmbedVideoLink } from '../components/EnableEmbedVideoModal';
import { PageEntrance, StaggeredList } from '../components/ui';
import { api, triggerScan, getScanUsage, createBulkJob, enableWebMcp, disableWebMcp, ApiError } from '../services/api';
import './DashboardPage.css';
import type { ScanUsageResponse } from '../services/api';
import type { Tier } from '../types';
import { useScanContext } from '../providers/ScanContext';
import { useScanStatus } from '../hooks/useScanStatus';
import { useStoreSettings } from '../hooks/useStoreSettings';
import { useTierConfig } from '../providers/TierConfigProvider';
import { getScoreTierConfig } from '../theme';
import { formatRelativeOrDate } from '../utils/dateFormat';

/**
 * T083: DashboardPage.
 * Store ScoreCard, TrendChart, DistributionHistogram, top 10 worst products,
 * issues by type, issues fixed this week, rescan button.
 */

interface DashboardResponse {
  store: {
    id: string;
    shopDomain: string;
    name: string;
    tier: string;
    overallScore: number | null;
    grade: string | null;
    scoreTier: string | null;
    colour: string | null;
    productCount: number;
    lastScanAt: string | null;
    lastScanJobId: string | null;
    algoVersion: string;
  };
  scoreDistribution: { excellent: number; good: number; warning: number; critical: number };
  trendLine: Array<{ date: string; score: number }>;
  worstProducts: Array<{
    id: string;
    shopifyProductId: string;
    title: string;
    overallScore: number;
    grade: string;
    tier: string;
    colour: string;
    imageUrl: string | null;
    updatedAt: string;
    storefrontUrl: string | null;
  }>;
  issuesByType: {
    dataCompleteness: number;
    schemaQuality: number;
    identifier: number;
    textQuality: number;
    media: number;
    consistency: number;
  };
  issuesFixedThisWeek: number;
  summaryStats: {
    withSummary: number;
    withoutSummary: number;
    stale: number;
    lastGeneratedAt: string | null;
  };
  platformReadiness: {
    chatgpt: {
      scoredCount: number;
      avgScore: number | null;
      discoverable: number;
      readable: number;
      confusing: number;
      invisible: number;
      searchEligible: number;
      notSearchEligible: number;
    };
    storeReadiness: {
      configScore: number | null;
      openAiStoreScore: number | null;
      hasReturnPolicy: boolean;
      hasPrivacyPolicy: boolean;
      hasTermsOfService: boolean;
      privacyPolicyPageUrl?: string | null;
      termsOfServicePageUrl?: string | null;
      returnPolicyPageUrl?: string | null;
    };
    gemini: {
      scoredCount: number;
      avgScore: number | null;
      discoverable: number;
      readable: number;
      confusing: number;
      invisible: number;
    };
    storeUcpReadiness: {
      configScore: number | null;
      geminiUcpStoreScore: number | null;
      ucpProfilePublished: boolean;
      hasPrivacyPolicy: boolean;
      hasTermsOfService: boolean;
    };
  };
  webMcp: {
    enabled: boolean;
    appEmbedEnabled: boolean;
    live: boolean;
  };
}

const ISSUE_TYPE_LABELS: Record<string, string> = {
  dataCompleteness: 'Data Completeness',
  schemaQuality: 'Schema Quality',
  identifier: 'Identifier',
  textQuality: 'Text Quality',
  media: 'Media',
  consistency: 'Consistency',
};

const STORE_POLICY_FIX_ITEMS: { label: string; platforms: string; how: string }[] = [
  { label: 'Privacy policy', platforms: 'ChatGPT, Gemini', how: 'Shopify Admin → Settings → Policies → add a Privacy policy and save.' },
  { label: 'Terms of service', platforms: 'ChatGPT, Gemini', how: 'Shopify Admin → Settings → Policies → add Terms of service and save.' },
  { label: 'Return policy', platforms: 'ChatGPT', how: 'Shopify Admin → Settings → Policies → add a Refund policy and save.' },
  { label: 'UCP profile (/.well-known/ucp)', platforms: 'Gemini', how: 'Publish a Universal Commerce Profile JSON at /.well-known/ucp on your storefront domain. See Gemini UCP documentation for the schema.' },
];

function useDashboard() {
  return useQuery<DashboardResponse>({
    queryKey: ['dashboard'],
    queryFn: () => api.get<DashboardResponse>('/store/dashboard'),
    staleTime: 30_000,
  });
}

function useScanUsage() {
  return useQuery<ScanUsageResponse>({
    queryKey: ['scan-usage'],
    queryFn: () => getScanUsage(),
    staleTime: 30_000,
  });
}

export function DashboardPage(): React.ReactElement | null {
  const navigate = useNavigate();
  const { data, isLoading, error } = useDashboard();
  const { data: scanUsage } = useScanUsage();

  // T039 + T040: Scan status awareness
  const { activeJobId, isScanning, clearScan } = useScanContext();
  const { data: scanProgress } = useScanStatus(activeJobId);
  const [showCompleteBanner, setShowCompleteBanner] = useState(false);

  // T040: Show notification banner when scan transitions to COMPLETED
  useEffect(() => {
    if (scanProgress?.status === 'COMPLETED') {
      setShowCompleteBanner(true);
      clearScan();
    }
  }, [scanProgress?.status, clearScan]);

  // T039: Detect partial results — scan was cancelled or is still running
  const isPartialResults =
    scanProgress?.status === 'CANCELLED' ||
    scanProgress?.status === 'RUNNING';
  const scannedProducts = scanProgress?.scannedProducts ?? 0;

  const productSuffix = scannedProducts === 1 ? '' : 's';

  // T042 + T043: Scan scope state
  const [scanTriggerError, setScanTriggerError] = useState<string | null>(null);
  const [conflictJobId, setConflictJobId] = useState<string | null>(null);
  const [scanTriggerSource, setScanTriggerSource] = useState<'hero' | 'card' | null>(null);
  const { settings: storeSettings } = useStoreSettings();
  const { getLimits } = useTierConfig();

  // Bulk AI summary generation
  const queryClient = useQueryClient();
  const bulkSummaryMutation = useMutation({
    mutationFn: () => createBulkJob({ jobType: 'SUMMARY_GENERATE' }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      navigate(`/summary-progress/${data.jobId}`);
    },
  });

  const [acpHelpOpen, setAcpHelpOpen] = useState(false);
  const [acpFixOpen, setAcpFixOpen] = useState(false);
  const [storeConfigOpen, setStoreConfigOpen] = useState(false);
  const [webMcpMenuOpen, setWebMcpMenuOpen] = useState(false);
  // Warning dialog shown when a policy exists as an Online Store page but isn't
  // configured under Settings → Policies (so it isn't served to ACP/UCP).
  const [policyPageWarning, setPolicyPageWarning] = useState<{ label: string; pageUrl: string } | null>(null);

  // Combined store configuration refresh — recomputes both ACP and UCP scores in one call.
  const storeConfigRefreshMutation = useMutation({
    mutationFn: () => api.post<{
      acpConfigScore: number | null;
      ucpConfigScore: number | null;
      openAiStoreScore: number | null;
      geminiUcpStoreScore: number | null;
      signals: {
        hasReturnPolicy: boolean;
        hasPrivacyPolicy: boolean;
        hasTermsOfService: boolean;
        ucpProfilePublished: boolean;
      };
    }>('/store/store-config-refresh'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  // WebMCP enable/disable toggle (paid tier only)
  const webMcpMutation = useMutation({
    mutationFn: async (enable: boolean) => {
      if (enable) await enableWebMcp();
      else await disableWebMcp();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const handleTriggerScan = useCallback(
    async (source: 'hero' | 'card' = 'card') => {
      setScanTriggerError(null);
      setConflictJobId(null);
      setScanTriggerSource(source);
      try {
        const result = await triggerScan({
          emailNotify: false,
          fromOnboarding: false,
        });
        navigate(`/scan-progress/${result.jobId}`);
      } catch (err: unknown) {
        // T054: Detect 409 conflict (active scan already in progress)
        if (err instanceof ApiError && err.status === 409) {
          // Try to parse activeJobId from the error response
          try {
            const parsed = JSON.parse(err.message);
            if (parsed.activeJobId) {
              setConflictJobId(parsed.activeJobId);
            }
          } catch {
            // Ignore parsing error
          }
          setScanTriggerError('A scan is already in progress.');
        } else {
          const message = err instanceof Error ? err.message : 'Failed to start scan';
          setScanTriggerError(message);
        }
      }
    },
    [navigate],
  );

  if (isLoading) {
    return null;
  }

  if (error || !data) {
    return (
      <Page title="Dashboard">
        <Banner tone="critical">
          <p>{error?.message ?? 'Unable to load dashboard.'}</p>
        </Banner>
      </Page>
    );
  }

  const { store, scoreDistribution, trendLine, worstProducts, issuesByType, issuesFixedThisWeek, summaryStats } = data;

  // ── AI Platform Readiness tier gate ───────────────────────────
  // FREE tier sees the section rendered with grayed-out mock data and disabled
  // controls instead of the real scores (backend also skips compute for FREE).
  const isAiReadinessLocked = store.tier === 'FREE';
  const MOCK_PLATFORM_READINESS: DashboardResponse['platformReadiness'] = {
    chatgpt: {
      scoredCount: 12,
      avgScore: 62,
      discoverable: 3,
      readable: 5,
      confusing: 3,
      invisible: 1,
      searchEligible: 8,
      notSearchEligible: 4,
    },
    storeReadiness: {
      configScore: 60,
      openAiStoreScore: null,
      hasReturnPolicy: true,
      hasPrivacyPolicy: true,
      hasTermsOfService: false,
    },
    gemini: {
      scoredCount: 12,
      avgScore: 55,
      discoverable: 2,
      readable: 6,
      confusing: 3,
      invisible: 1,
    },
    storeUcpReadiness: {
      configScore: 50,
      geminiUcpStoreScore: null,
      ucpProfilePublished: false,
      hasPrivacyPolicy: true,
      hasTermsOfService: false,
    },
  };
  const platformReadiness = isAiReadinessLocked ? MOCK_PLATFORM_READINESS : data.platformReadiness;
  // WebMCP is a paid feature; the locked teaser shows it as not-yet-enabled.
  // "Live" requires both the in-app toggle AND the Zelium app embed being on in the theme.
  const webMcpLive = isAiReadinessLocked ? false : data.webMcp.live;

  // Worst products table rows
  const worstRows = worstProducts.map((p) => {
    const tierConfig = getScoreTierConfig(p.overallScore);
    return [
      <div key={`product-${p.id}`} className="dash-worst-product-cell">
        <div className="dash-worst-product-thumb">
          <Thumbnail
            source={p.imageUrl ?? 'placeholder'}
            alt=""
            size="extraSmall"
          />
        </div>
        <Button
          key={`title-${p.id}`}
          variant="plain"
          textAlign="start"
          onClick={() => navigate(`/products/${p.id}`)}
        >
          {p.title}
        </Button>
      </div>,
      p.overallScore,
      <Badge key={`tier-${p.id}`} tone={tierConfig.badgeTone}>
        {tierConfig.label}
      </Badge>,
      p.storefrontUrl ? (
        <a
          key={`store-${p.id}`}
          href={p.storefrontUrl}
          target="_blank"
          rel="noopener noreferrer"
          title="View on Store"
        >
          <Icon source={ExternalIcon} tone="interactive" />
        </a>
      ) : '—',
    ];
  });

  // Issues by type rows
  const issueRows = Object.entries(issuesByType)
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a)
    .map(([type, count]) => [ISSUE_TYPE_LABELS[type] ?? type, count]);

  const totalIssues = Object.values(issuesByType).reduce((s, c) => s + c, 0);

  return (
    <PageEntrance>
    <Page title="Store Dashboard" subtitle={store.shopDomain}>
      <BlockStack gap="400">
        {/* Hero CTA — sells the AI score value */}
        <InlineStack align="end">
          <Button variant="plain" icon={QuestionCircleIcon} onClick={() => navigate('/faq')}>FAQs</Button>
        </InlineStack>
        <div className="z-hero">
          <div className="z-hero-content">
            <h2 className="z-hero-title">🧠 Discover your AI Discoverability Score</h2>
            <p className="z-hero-subtitle">
              See exactly how AI engines like ChatGPT, Perplexity, and Google Gemini view your products — and what to fix.
            </p>
            <div className="z-hero-buttons">
              <button
                type="button"
                className="z-btn-primary"
                onClick={() => handleTriggerScan('hero')}
                disabled={isScanning}
              >
                🔬 Run Scan
              </button>
              <button
                type="button"
                className="z-btn-secondary"
                onClick={() => navigate('/products')}
              >
                📋 View All Products
              </button>
            </div>
            {scanTriggerError && scanTriggerSource === 'hero' && (
              <div className="z-hero-scan-error">
                <span>
                  {scanTriggerError}
                  {conflictJobId && (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="z-btn-link"
                        onClick={() => navigate(`/scan-progress/${conflictJobId}`)}
                      >
                        View current scan progress
                      </button>
                    </>
                  )}
                </span>
                <button
                  type="button"
                  className="z-hero-scan-error__dismiss"
                  aria-label="Dismiss"
                  onClick={() => setScanTriggerError(null)}
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        </div>

        {/* T040: Scan completion notification banner */}
        {showCompleteBanner && (
          <Banner
            title="Scan complete"
            tone="success"
            onDismiss={() => setShowCompleteBanner(false)}
          >
            <p>
              Your scan has finished.{' '}
              {activeJobId ? (
                <Button
                  variant="plain"
                  onClick={() => navigate(`/scan-complete/${activeJobId}`)}
                >
                  View results
                </Button>
              ) : (
                'Refresh to see your updated scores.'
              )}
            </p>
          </Banner>
        )}

        {/* T039: Partial results banner when scan is still running or was cancelled */}
        {isPartialResults && (
          <Banner
            title="Preliminary score"
            tone="warning"
          >
            <p>
              {scanProgress?.status === 'RUNNING'
                ? `A scan is in progress. Showing preliminary score based on ${scannedProducts} product${productSuffix} scanned so far.`
                : `Scan was cancelled. Showing partial score based on ${scannedProducts} product${productSuffix}.`}
            </p>
          </Banner>
        )}

        {/* T040: Active scan indicator on dashboard */}
        {isScanning && activeJobId && (
          <Banner tone="info">
            <InlineStack gap="200" blockAlign="center">
              <Text as="span" variant="bodySm">
                Scan in progress…
              </Text>
              <Button
                variant="plain"
                size="slim"
                onClick={() => navigate(`/scan-progress/${activeJobId}`)}
              >
                View progress
              </Button>
            </InlineStack>
          </Banner>
        )}
      <Layout>
        {/* Store Score Card + Rescan */}
        <Layout.Section variant="oneThird">
          <StaggeredList>
            {store.overallScore != null && store.productCount > 0 ? (
              <ScoreCard
                score={store.overallScore ?? 0}
                colour={(store.colour ?? 'red') as any}
                productCount={store.productCount}
                storeName={store.name}
              >
                <BlockStack gap="200" inlineAlign="center">
                  {store.lastScanAt && (
                    <Text as="span" variant="bodySm" tone="subdued">
                      Last Scan: {formatRelativeOrDate(store.lastScanAt)}
                    </Text>
                  )}
                  {store.lastScanJobId && (
                    <Button
                      variant="plain"
                      onClick={() => navigate(`/scan-complete/${store.lastScanJobId}`)}
                    >
                      View full report
                    </Button>
                  )}
                </BlockStack>
              </ScoreCard>
            ) : (
              <Card>
                <BlockStack gap="300" inlineAlign="center">
                  <Text as="h2" variant="headingMd">
                    {store.name}
                  </Text>
                  <Box padding="400">
                    <BlockStack gap="200" inlineAlign="center">
                      <Text as="p" variant="bodyLg" fontWeight="semibold">
                        No score available yet
                      </Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        {store.productCount === 0
                          ? 'No products have been scanned. Run a scan to see your AI discoverability score.'
                          : 'Your store has not been scored yet. Run a scan to get started.'}
                      </Text>
                    </BlockStack>
                  </Box>
                </BlockStack>
              </Card>
            )}

            {/* Scan options card */}
            <Card>
              <BlockStack gap="300">
                <Text as="h3" variant="headingSm">🔬 Scan</Text>
                {store.tier === 'FREE' ? (
                  <>
                    <InlineStack gap="200" blockAlign="center">
                      <TierBadge tier={store.tier} />
                      <Text as="span" variant="bodySm">{storeSettings?.productLimit ?? store.productCount} pinned products</Text>
                    </InlineStack>
                    {scanUsage && (
                      <Text as="span" variant="bodySm" tone="subdued">
                        Scans: {scanUsage.scansMax - scanUsage.scansUsed} of {scanUsage.scansMax} remaining this cycle
                      </Text>
                    )}
                    <Button
                      variant="primary"
                      onClick={() => handleTriggerScan('card')}
                      disabled={isScanning || (scanUsage != null && scanUsage.scansUsed >= scanUsage.scansMax)}
                    >
                      Scan{scanUsage ? ` (${scanUsage.scansMax - scanUsage.scansUsed} left)` : ''}
                    </Button>
                    <Button
                      onClick={() => navigate('/billing')}
                      icon={<Icon source={LockIcon} />}
                      variant="plain"
                    >
                      Upgrade for more scans
                    </Button>
                  </>
                ) : (
                  <>
                    <InlineStack gap="200" blockAlign="center">
                      <TierBadge tier={store.tier as Tier} />
                      <Text as="span" variant="bodySm">Full catalog access</Text>
                    </InlineStack>
                    <Button
                      variant="primary"
                      onClick={() => handleTriggerScan('card')}
                      disabled={isScanning}
                    >
                      Scan
                    </Button>
                  </>
                )}
                {scanTriggerError && scanTriggerSource === 'card' && (
                  <Banner tone="critical">
                    <p>
                      {scanTriggerError}
                      {conflictJobId && (
                        <>
                          {' '}
                          <Button
                            variant="plain"
                            onClick={() => navigate(`/scan-progress/${conflictJobId}`)}
                          >
                            View current scan progress
                          </Button>
                        </>
                      )}
                    </p>
                  </Banner>
                )}
              </BlockStack>
            </Card>

            {/* Issues Overview — same column as Scan Scope */}
            <Card>
              <BlockStack gap="300">
                <InlineStack align="space-between">
                  <Text as="h2" variant="headingMd">Issues Overview</Text>
                  <Badge tone="info">{`${totalIssues} total`}</Badge>
                </InlineStack>

                <InlineStack align="space-between">
                  <Text as="span" variant="bodySm">Fixed This Week</Text>
                  <Badge tone="success">{String(issuesFixedThisWeek)}</Badge>
                </InlineStack>

                {issueRows.length > 0 && (
                  <DataTable
                    columnContentTypes={['text', 'numeric']}
                    headings={['Category', 'Count']}
                    rows={issueRows}
                  />
                )}
              </BlockStack>
            </Card>

            {/* AI Summaries — bulk generate, tier-gated */}
            <Card>
              <BlockStack gap="300">
                <InlineStack align="space-between">
                  <Text as="h3" variant="headingSm">AI Summaries</Text>
                  {summaryStats && store.productCount > 0 && (
                    <Badge tone={summaryStats.withSummary === store.productCount ? 'success' : 'attention'}>
                      {`${summaryStats.withSummary}/${store.productCount}`}
                    </Badge>
                  )}
                </InlineStack>
                <Text as="p" variant="bodySm" tone="subdued">
                  Generate AI-powered product summaries for all scanned products.
                  Summaries make your products discoverable by AI assistants like
                  ChatGPT, Perplexity, and Google AI Overviews.
                </Text>

                {/* Summary statistics */}
                {summaryStats && store.productCount > 0 && (
                  <BlockStack gap="200">
                    <ProgressBar
                      progress={store.productCount > 0 ? Math.round((summaryStats.withSummary / store.productCount) * 100) : 0}
                      size="small"
                      tone="primary"
                    />
                    <InlineStack align="space-between">
                      <Text as="span" variant="bodySm" tone="subdued">With summary</Text>
                      <Text as="span" variant="bodySm" fontWeight="semibold">{summaryStats.withSummary}</Text>
                    </InlineStack>
                    <InlineStack align="space-between">
                      <Text as="span" variant="bodySm" tone="subdued">Without summary</Text>
                      <Text as="span" variant="bodySm" fontWeight="semibold">{summaryStats.withoutSummary}</Text>
                    </InlineStack>
                    {summaryStats.stale > 0 && (
                      <InlineStack align="space-between">
                        <Text as="span" variant="bodySm" tone="caution">Stale (product changed)</Text>
                        <Badge tone="warning">{String(summaryStats.stale)}</Badge>
                      </InlineStack>
                    )}
                    {summaryStats.lastGeneratedAt && (
                      <InlineStack align="space-between">
                        <Text as="span" variant="bodySm" tone="subdued">Last generated</Text>
                        <Text as="span" variant="bodySm">{formatRelativeOrDate(summaryStats.lastGeneratedAt)}</Text>
                      </InlineStack>
                    )}
                    <InlineStack align="space-between">
                      <Text as="span" variant="bodySm" tone="subdued">Auto-refresh</Text>
                      <Text as="span" variant="bodySm">On product change</Text>
                    </InlineStack>
                  </BlockStack>
                )}

                {(store.tier === 'FREE' || store.tier === 'STARTER') ? (
                  <Button
                    onClick={() => navigate('/billing')}
                    icon={<Icon source={LockIcon} />}
                    variant="plain"
                  >
                    Upgrade to Growth to generate AI summaries
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="primary"
                      onClick={() => bulkSummaryMutation.mutate()}
                      loading={bulkSummaryMutation.isPending}
                      disabled={bulkSummaryMutation.isPending || store.productCount === 0}
                    >
                      {(() => {
                        if (summaryStats && summaryStats.stale > 0) {
                          return `Regenerate ${summaryStats.stale} Stale Summaries`;
                        }
                        if (summaryStats && summaryStats.withoutSummary > 0) {
                          return `Generate ${summaryStats.withoutSummary} Missing Summaries`;
                        }
                        return 'Generate All Summaries';
                      })()}
                    </Button>
                    {bulkSummaryMutation.isError && (
                      <Banner tone="critical">
                        <p>{bulkSummaryMutation.error?.message ?? 'Failed to start bulk generation'}</p>
                      </Banner>
                    )}
                  </>
                )}
              </BlockStack>
            </Card>

            {/* WebMCP — enable agentic tools on the storefront, tier-gated (Starter+) */}
            <Card>
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center">
                  <InlineStack gap="200" blockAlign="center">
                    <Text as="h3" variant="headingSm">WebMCP</Text>
                    <Badge tone="magic">Most advanced</Badge>
                  </InlineStack>
                  {store.tier !== 'FREE' && data.webMcp.enabled && (
                    <InlineStack gap="150" blockAlign="center">
                      {data.webMcp.live ? (
                        <Badge tone="success" icon={CheckIcon}>Enabled</Badge>
                      ) : (
                        <Badge tone="warning">Embed off</Badge>
                      )}
                      <Popover
                        active={webMcpMenuOpen}
                        onClose={() => setWebMcpMenuOpen(false)}
                        preferredAlignment="right"
                        activator={
                          <div>
                            <Button
                              variant="tertiary"
                              icon={MenuHorizontalIcon}
                              onClick={() => setWebMcpMenuOpen((v) => !v)}
                              loading={webMcpMutation.isPending}
                              accessibilityLabel="More WebMCP options"
                            />
                          </div>
                        }
                      >
                        <ActionList
                          actionRole="menuitem"
                          items={[
                            {
                              content: 'Disable WebMCP',
                              destructive: true,
                              onAction: () => {
                                setWebMcpMenuOpen(false);
                                webMcpMutation.mutate(false);
                              },
                            },
                          ]}
                        />
                      </Popover>
                    </InlineStack>
                  )}
                </InlineStack>
                <Text as="p" variant="bodySm" tone="subdued">
                  Let AI shopping assistants find and recommend your products. When shoppers use
                  AI-powered browsers like Chrome and Gemini, they can search your store and get the
                  right products instantly — so you capture sales from the growing wave of AI-assisted shopping.
                </Text>

                {/* Enabled in-app but the app embed is off → the tool won't load on the storefront. */}
                {store.tier !== 'FREE' && data.webMcp.enabled && !data.webMcp.appEmbedEnabled && (
                  <Banner tone="warning">
                    <BlockStack gap="100">
                      <p>
                        WebMCP is enabled in Zelium, but the <strong>Zelium AI Structured Data</strong> app
                        embed is turned off in your theme — the tool won&apos;t load. Enable it in{' '}
                        <strong>Online Store → Themes → Customize → App embeds</strong>.
                      </p>
                      <div>
                        <EnableEmbedVideoLink />
                      </div>
                    </BlockStack>
                  </Banner>
                )}

                {store.tier === 'FREE' ? (
                  <Button
                    onClick={() => navigate('/billing')}
                    icon={<Icon source={LockIcon} />}
                    variant="plain"
                  >
                    Upgrade to Starter to enable WebMCP
                  </Button>
                ) : !data.webMcp.enabled ? (
                  <>
                    <Button
                      variant="primary"
                      onClick={() => webMcpMutation.mutate(true)}
                      loading={webMcpMutation.isPending}
                      disabled={webMcpMutation.isPending}
                    >
                      Enable WebMCP
                    </Button>
                    {webMcpMutation.isError && (
                      <Banner tone="critical">
                        <p>{webMcpMutation.error?.message ?? 'Failed to update WebMCP'}</p>
                      </Banner>
                    )}
                  </>
                ) : (
                  webMcpMutation.isError && (
                    <Banner tone="critical">
                      <p>{webMcpMutation.error?.message ?? 'Failed to update WebMCP'}</p>
                    </Banner>
                  )
                )}
              </BlockStack>
            </Card>

            <AppEmbedStatusCard />
          </StaggeredList>
        </Layout.Section>

        {/* Trend Chart + Score Distribution + Bottom 10 */}
        <Layout.Section>
          <BlockStack gap="400">
            <TrendChart data={trendLine} maxDays={getLimits(store.tier as any)?.historyDays ?? 7} />
            <DistributionHistogram distribution={scoreDistribution} />

            {/* AI Platform Readiness */}
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h2" variant="headingMd">AI Platform Readiness</Text>
                  <Button
                    variant="plain"
                    icon={QuestionCircleIcon}
                    onClick={() => setAcpHelpOpen(true)}
                    accessibilityLabel="AI platform readiness explained"
                  />
                </InlineStack>
                {isAiReadinessLocked && (
                  <Banner
                    tone="info"
                    action={{ content: 'View plans', url: '/billing' }}
                  >
                    <Text as="p" variant="bodySm">
                      Upgrade to <Text as="span" fontWeight="semibold">Starter</Text> or higher to unlock AI Platform Readiness — see how ChatGPT and Gemini score your products and storefront, and how to fix what&apos;s holding them back.
                    </Text>
                  </Banner>
                )}
                <div className={isAiReadinessLocked ? 'dash-ai-readiness-locked' : undefined}>
                  <BlockStack gap="400">
                {([
                  { key: 'chatgpt', label: 'ChatGPT', stats: platformReadiness.chatgpt },
                ] as const).map(({ key, label, stats }) => (
                  <BlockStack key={key} gap="200">
                    <BlockStack gap="100">
                      <InlineStack align="space-between">
                        <Text as="span" variant="bodySm" fontWeight="medium">
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0 }}>
                              <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.677l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0L4.001 14.614A4.5 4.5 0 0 1 2.34 7.896zm16.597 3.855-5.843-3.387L15.115 7.2a.076.076 0 0 1 .071 0l4.816 2.786a4.494 4.494 0 0 1-.676 8.105V12.48a.795.795 0 0 0-.39-.73zm2.01-3.023-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.08.08 0 0 1 .032-.062l4.808-2.782a4.496 4.496 0 0 1 6.67 4.66zm-12.64 4.135-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.496 4.496 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365 2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
                            </svg>
                            <span>{label}</span>
                          </span>
                        </Text>
                        <Text
                          as="span"
                          variant="bodySm"
                          fontWeight="semibold"
                          tone={stats.avgScore !== null && stats.avgScore < 40 ? 'critical' : undefined}
                        >
                          {stats.avgScore !== null ? stats.avgScore : '—'}
                        </Text>
                      </InlineStack>
                      <ProgressBar
                        progress={stats.avgScore ?? 0}
                        size="small"
                        tone={
                          stats.avgScore === null ? 'highlight'
                          : stats.avgScore >= 60 ? 'success'
                          : stats.avgScore >= 40 ? 'highlight'
                          : 'critical'
                        }
                      />
                    </BlockStack>
                    {stats.scoredCount === 0 ? (
                      <Text as="p" variant="bodySm" tone="subdued">Run a scan to compute readiness scores.</Text>
                    ) : (
                      <InlineStack gap="400" wrap blockAlign="center">
                          {stats.discoverable > 0 ? (
                            <Link onClick={() => navigate('/products?acpScoreMin=80&acpScoreMax=100&sortBy=score_desc')}>
                              {`${stats.discoverable} Discoverable`}
                            </Link>
                          ) : (
                            <Text as="span" variant="bodySm" tone="subdued">{`${stats.discoverable} Discoverable`}</Text>
                          )}
                          {stats.readable > 0 ? (
                            <Link onClick={() => navigate('/products?acpScoreMin=60&acpScoreMax=79&sortBy=score_desc')}>
                              {`${stats.readable} Readable`}
                            </Link>
                          ) : (
                            <Text as="span" variant="bodySm" tone="subdued">{`${stats.readable} Readable`}</Text>
                          )}
                          {stats.confusing > 0 ? (
                            <Link onClick={() => navigate('/products?acpScoreMin=40&acpScoreMax=59&sortBy=score_asc')}>
                              {`${stats.confusing} Confusing`}
                            </Link>
                          ) : (
                            <Text as="span" variant="bodySm" tone="subdued">{`${stats.confusing} Confusing`}</Text>
                          )}
                          {stats.invisible > 0 ? (
                            <Link onClick={() => navigate('/products?acpScoreMin=0&acpScoreMax=39&sortBy=score_asc')}>
                              {`${stats.invisible} Invisible`}
                            </Link>
                          ) : (
                            <Text as="span" variant="bodySm" tone="subdued">{`${stats.invisible} Invisible`}</Text>
                          )}
                          <Text as="span" variant="bodySm" tone="subdued">·</Text>
                          <InlineStack gap="100" blockAlign="center">
                            <Icon source={CheckCircleIcon} tone="success" />
                            <Text as="span" variant="bodySm" tone="success">{stats.searchEligible} eligible</Text>
                          </InlineStack>
                          <InlineStack gap="100" blockAlign="center">
                            <Icon source={XCircleIcon} tone="critical" />
                            <Text as="span" variant="bodySm" tone="critical">{stats.notSearchEligible} not eligible</Text>
                          </InlineStack>
                        </InlineStack>
                    )}
                  </BlockStack>
                ))}

                {/* Store-level ACP config score (now rendered as part of the combined "Store Configuration" section below) */}
                {(() => null)()}

                {/* ── Gemini (UCP) row ── */}
                <Divider />
                <BlockStack gap="200">
                  <BlockStack gap="100">
                    <InlineStack align="space-between">
                      <Text as="span" variant="bodySm" fontWeight="medium">
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <svg width="14" height="14" viewBox="0 0 192 192" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
                            <path d="M96 0C96 52.896 52.896 96 0 96c52.896 0 96 43.104 96 96 0-52.896 43.104-96 96-96-52.896 0-96-43.104-96-96Z" fill="url(#gemini-dash-grad)"/>
                            <defs>
                              <linearGradient id="gemini-dash-grad" x1="0" y1="0" x2="192" y2="192" gradientUnits="userSpaceOnUse">
                                <stop stopColor="#4285F4"/>
                                <stop offset="1" stopColor="#34A853"/>
                              </linearGradient>
                            </defs>
                          </svg>
                          <span>Gemini</span>
                        </span>
                      </Text>
                      <Text
                        as="span"
                        variant="bodySm"
                        fontWeight="semibold"
                        tone={platformReadiness.gemini.avgScore !== null && platformReadiness.gemini.avgScore < 40 ? 'critical' : undefined}
                      >
                        {platformReadiness.gemini.avgScore !== null ? platformReadiness.gemini.avgScore : '—'}
                      </Text>
                    </InlineStack>
                    <ProgressBar
                      progress={platformReadiness.gemini.avgScore ?? 0}
                      size="small"
                      tone={
                        platformReadiness.gemini.avgScore === null ? 'highlight'
                        : platformReadiness.gemini.avgScore >= 60 ? 'success'
                        : platformReadiness.gemini.avgScore >= 40 ? 'highlight'
                        : 'critical'
                      }
                    />
                  </BlockStack>
                  {platformReadiness.gemini.scoredCount === 0 ? (
                    <Text as="p" variant="bodySm" tone="subdued">Run a scan to compute Gemini readiness scores.</Text>
                  ) : (
                    <InlineStack gap="400" wrap blockAlign="center">
                      {platformReadiness.gemini.discoverable > 0 ? (
                        <Link onClick={() => navigate('/products?ucpScoreMin=80&ucpScoreMax=100&sortBy=score_desc')}>
                          {`${platformReadiness.gemini.discoverable} Discoverable`}
                        </Link>
                      ) : (
                        <Text as="span" variant="bodySm" tone="subdued">{`${platformReadiness.gemini.discoverable} Discoverable`}</Text>
                      )}
                      {platformReadiness.gemini.readable > 0 ? (
                        <Link onClick={() => navigate('/products?ucpScoreMin=60&ucpScoreMax=79&sortBy=score_desc')}>
                          {`${platformReadiness.gemini.readable} Readable`}
                        </Link>
                      ) : (
                        <Text as="span" variant="bodySm" tone="subdued">{`${platformReadiness.gemini.readable} Readable`}</Text>
                      )}
                      {platformReadiness.gemini.confusing > 0 ? (
                        <Link onClick={() => navigate('/products?ucpScoreMin=40&ucpScoreMax=59&sortBy=score_asc')}>
                          {`${platformReadiness.gemini.confusing} Confusing`}
                        </Link>
                      ) : (
                        <Text as="span" variant="bodySm" tone="subdued">{`${platformReadiness.gemini.confusing} Confusing`}</Text>
                      )}
                      {platformReadiness.gemini.invisible > 0 ? (
                        <Link onClick={() => navigate('/products?ucpScoreMin=0&ucpScoreMax=39&sortBy=score_asc')}>
                          {`${platformReadiness.gemini.invisible} Invisible`}
                        </Link>
                      ) : (
                        <Text as="span" variant="bodySm" tone="subdued">{`${platformReadiness.gemini.invisible} Invisible`}</Text>
                      )}
                    </InlineStack>
                  )}
                </BlockStack>

                {/* Combined store configuration — replaces the legacy split "Store Policy Score" (ACP) and "Gemini Store Score" (UCP) cards. */}
                {(() => {
                  const sr = platformReadiness.storeReadiness;
                  const su = platformReadiness.storeUcpReadiness;
                  const acpScore = sr.configScore;
                  const ucpScore = su.configScore;
                  const bothNull = acpScore === null && ucpScore === null;

                  const acpTone =
                    acpScore === null ? 'highlight'
                    : acpScore >= 80 ? 'success'
                    : acpScore >= 50 ? 'highlight'
                    : 'critical';
                  const ucpTone =
                    ucpScore === null ? 'highlight'
                    : ucpScore >= 80 ? 'success'
                    : ucpScore >= 40 ? 'highlight'
                    : 'critical';

                  // pageUrl: a policy authored as an Online Store page but NOT configured under
                  // Settings → Policies. Only set when the configured policy is missing — surfaced
                  // as a warning because Shopify doesn't send stand-alone pages to ACP/UCP.
                  const signals: { label: string; present: boolean; platforms: string; pageUrl?: string | null }[] = [
                    { label: 'Privacy policy', present: sr.hasPrivacyPolicy, platforms: 'ChatGPT, Gemini', pageUrl: sr.privacyPolicyPageUrl },
                    { label: 'Terms of service', present: sr.hasTermsOfService, platforms: 'ChatGPT, Gemini', pageUrl: sr.termsOfServicePageUrl },
                    { label: 'Return policy', present: sr.hasReturnPolicy, platforms: 'ChatGPT', pageUrl: sr.returnPolicyPageUrl },
                    { label: 'UCP profile (/.well-known/ucp)', present: su.ucpProfilePublished, platforms: 'Gemini' },
                    { label: 'WebMCP agentic tools', present: webMcpLive, platforms: 'Chrome / Gemini agents' },
                  ];

                  return (
                    <BlockStack gap="200">
                      <Divider />
                      <InlineStack align="space-between" blockAlign="center">
                        <InlineStack gap="150" blockAlign="center">
                          <Icon source={StoreIcon} tone="subdued" />
                          <Text as="span" variant="bodySm" fontWeight="medium">Store Configuration</Text>
                        </InlineStack>
                        <InlineStack gap="200" blockAlign="center">
                          <Button
                            variant="plain"
                            size="slim"
                            icon={RefreshIcon}
                            onClick={() => storeConfigRefreshMutation.mutate()}
                            loading={storeConfigRefreshMutation.isPending}
                            accessibilityLabel="Refresh store configuration"
                          >
                            Recheck
                          </Button>
                        </InlineStack>
                      </InlineStack>

                      {/* Per-platform score chips */}
                      <BlockStack gap="200">
                        <BlockStack gap="100">
                          <InlineStack align="space-between" blockAlign="center">
                            <Text as="span" variant="bodySm" tone="subdued">ChatGPT (ACP)</Text>
                            <Text as="span" variant="bodySm" fontWeight="semibold" tone={acpTone === 'critical' ? 'critical' : undefined}>
                              {acpScore !== null ? acpScore : '—'}
                            </Text>
                          </InlineStack>
                          <ProgressBar progress={acpScore ?? 0} size="small" tone={acpTone} />
                        </BlockStack>
                        <BlockStack gap="100">
                          <InlineStack align="space-between" blockAlign="center">
                            <Text as="span" variant="bodySm" tone="subdued">Gemini (UCP)</Text>
                            <Text as="span" variant="bodySm" fontWeight="semibold" tone={ucpTone === 'critical' ? 'critical' : undefined}>
                              {ucpScore !== null ? ucpScore : '—'}
                            </Text>
                          </InlineStack>
                          <ProgressBar progress={ucpScore ?? 0} size="small" tone={ucpTone} />
                        </BlockStack>
                      </BlockStack>

                      {bothNull ? (
                        <Text as="p" variant="bodySm" tone="subdued">Scores are computed automatically after your first product scan.</Text>
                      ) : (
                        <BlockStack gap="100">
                          <InlineStack align="space-between" blockAlign="center">
                            <Text as="span" variant="bodySm" tone="subdued">Signals</Text>
                            <Button
                              variant="plain"
                              size="slim"
                              icon={storeConfigOpen ? ChevronUpIcon : ChevronDownIcon}
                              onClick={() => setStoreConfigOpen((v) => !v)}
                              accessibilityLabel={storeConfigOpen ? 'Hide store configuration signals' : 'Show store configuration signals'}
                            >
                              {storeConfigOpen ? 'Hide' : 'Show'}
                            </Button>
                          </InlineStack>
                          <Collapsible
                            open={storeConfigOpen}
                            id="store-config-collapsible"
                            transition={{ duration: '150ms', timingFunction: 'ease-in-out' }}
                          >
                            <BlockStack gap="150">
                              {signals.map(({ label, present, platforms, pageUrl }) => {
                                // Found as a page but not configured under Settings → Policies.
                                const foundAsPage = !present && !!pageUrl;
                                return (
                                  <InlineStack key={label} align="space-between" blockAlign="center">
                                    <InlineStack gap="150" blockAlign="center">
                                      <Icon
                                        source={present ? CheckCircleIcon : foundAsPage ? AlertTriangleIcon : XCircleIcon}
                                        tone={present ? 'success' : foundAsPage ? 'warning' : 'critical'}
                                      />
                                      <Text as="span" variant="bodySm" tone={present ? undefined : 'subdued'}>{label}</Text>
                                    </InlineStack>
                                    {foundAsPage ? (
                                      <Link onClick={() => setPolicyPageWarning({ label, pageUrl: pageUrl as string })}>
                                        Found as page — review
                                      </Link>
                                    ) : (
                                      <Text as="span" variant="bodySm" tone="subdued">{`required by ${platforms}`}</Text>
                                    )}
                                  </InlineStack>
                                );
                              })}
                              <InlineStack align="end">
                                <Link onClick={() => setAcpFixOpen(true)}>How to fix?</Link>
                              </InlineStack>
                            </BlockStack>
                          </Collapsible>
                        </BlockStack>
                      )}
                    </BlockStack>
                  );
                })()}
                  </BlockStack>
                </div>
              </BlockStack>
            </Card>

            {/* Policy-found-as-page warning dialog */}
            <Modal
              open={policyPageWarning !== null}
              onClose={() => setPolicyPageWarning(null)}
              title={`${policyPageWarning?.label ?? 'Policy'} found, but not where AI platforms read it`}
              primaryAction={{
                content: 'View the page',
                onAction: () => {
                  if (policyPageWarning) window.open(policyPageWarning.pageUrl, '_blank', 'noopener,noreferrer');
                },
              }}
              secondaryActions={[{ content: 'Close', onAction: () => setPolicyPageWarning(null) }]}
            >
              <Modal.Section>
                <BlockStack gap="400">
                  <Text as="p" variant="bodyMd">
                    We found a <Text as="span" fontWeight="semibold">{policyPageWarning?.label}</Text> published as an Online Store page, but it is not configured under <Text as="span" fontWeight="semibold">Settings → Policies</Text>.
                  </Text>
                  <Text as="p" variant="bodyMd">
                    ChatGPT (ACP) and Gemini (UCP) receive your policies from the structured <Text as="span" fontWeight="semibold">Settings → Policies</Text> configuration — not from stand-alone pages. Even though the page URL works for shoppers, this policy <Text as="span" fontWeight="semibold">may not be served to the AI platforms</Text> and won&apos;t count toward your store readiness score.
                  </Text>
                  <Text as="p" variant="bodyMd">
                    To fix it, copy the content into <Text as="span" fontWeight="semibold">Shopify Admin → Settings → Policies</Text> and save. Then use <Text as="span" fontWeight="semibold">Recheck</Text> to update your score.
                  </Text>
                </BlockStack>
              </Modal.Section>
            </Modal>

            {/* AI Platform Readiness help modal */}
            <Modal
              open={acpHelpOpen}
              onClose={() => setAcpHelpOpen(false)}
              title="AI Platform Readiness Explained"
            >
              <Modal.Section>
                <BlockStack gap="400">
                  <Text as="p" variant="bodyMd">
                    AI Platform Readiness measures how well each product meets the data requirements of specific AI platforms. A higher score means the platform can confidently discover and recommend the product.
                  </Text>
                  <Divider />
                  <Text as="h3" variant="headingSm">ChatGPT Tiers</Text>
                  <BlockStack gap="300">
                    {([
                      { label: 'ChatGPT Discoverable', range: '80–100', tone: 'success' as const, desc: 'Fully meets OpenAI ACP feed requirements. ChatGPT will discover and confidently recommend this product in Shopping results.' },
                      { label: 'ChatGPT Readable', range: '60–79', tone: 'success' as const, desc: 'Meets most ACP requirements. Product may appear in ChatGPT recommendations with minor gaps.' },
                      { label: 'ChatGPT Confusing', range: '40–59', tone: 'attention' as const, desc: 'Partially meets ACP requirements. ChatGPT may misrepresent or skip this product in Shopping responses.' },
                      { label: 'ChatGPT Invisible', range: '0–39', tone: 'critical' as const, desc: 'Does not meet ACP requirements. Product is hidden from ChatGPT Shopping and will not appear in recommendations.' },
                    ]).map(({ label, range, tone, desc }) => (
                      <BlockStack key={label} gap="100">
                        <InlineStack gap="200" blockAlign="center">
                          <Badge tone={tone}>{label}</Badge>
                          <Text as="span" variant="bodySm" tone="subdued">{range}</Text>
                        </InlineStack>
                        <Text as="p" variant="bodySm">{desc}</Text>
                      </BlockStack>
                    ))}
                  </BlockStack>
                  <Divider />
                  <Text as="h3" variant="headingSm">Search Eligibility</Text>
                  <BlockStack gap="200">
                    <BlockStack gap="100">
                      <div className="dash-eligibility-label dash-eligibility-label--success">
                        <Icon source={CheckCircleIcon} tone="success" />
                        <Text as="span" variant="bodySm" fontWeight="medium" tone="success">
                          Search Eligible
                        </Text>
                      </div>
                      <Text as="p" variant="bodySm">Product has all four minimum fields required by the OpenAI ACP feed: brand/vendor, description, at least one image, and a SKU. These products can appear in ChatGPT Shopping search results.</Text>
                    </BlockStack>
                    <BlockStack gap="100">
                      <div className="dash-eligibility-label dash-eligibility-label--critical">
                        <Icon source={XCircleIcon} tone="critical" />
                        <Text as="span" variant="bodySm" fontWeight="medium" tone="critical">
                          Not Search Eligible
                        </Text>
                      </div>
                      <Text as="p" variant="bodySm">One or more required fields are missing. The product cannot appear in ChatGPT Shopping results until the gaps are fixed.</Text>
                    </BlockStack>
                  </BlockStack>
                </BlockStack>
              </Modal.Section>
            </Modal>

            {/* Store Configuration "How to fix?" modal */}
            <Modal
              open={acpFixOpen}
              onClose={() => setAcpFixOpen(false)}
              title="How to improve your Store Configuration"
            >
              <Modal.Section>
                <BlockStack gap="400">
                  <Text as="p" variant="bodyMd">
                    Your Store Configuration scores are based on the policies and platform-specific signals that ChatGPT (ACP) and Gemini (UCP) require. Completing each signal below increases the relevant platform's store score.
                  </Text>
                  <Divider />
                  <BlockStack gap="300">
                    {STORE_POLICY_FIX_ITEMS.map(({ label, platforms, how }) => (
                      <BlockStack key={label} gap="100">
                        <InlineStack gap="200" blockAlign="center">
                          <Badge tone="info">{`required by ${platforms}`}</Badge>
                          <Text as="span" variant="bodySm" fontWeight="semibold">{label}</Text>
                        </InlineStack>
                        <Text as="p" variant="bodySm" tone="subdued">{how}</Text>
                      </BlockStack>
                    ))}
                  </BlockStack>
                  <Divider />
                  <Text as="p" variant="bodySm" tone="subdued">
                    After making changes in Shopify Admin, click <strong>Recheck</strong> on the dashboard to refresh both scores immediately.
                  </Text>
                </BlockStack>
              </Modal.Section>
            </Modal>

            {/* Bottom 10 Products */}
            {worstProducts.length > 0 && (
              <Card>
                <BlockStack gap="200">
                  <Text as="h2" variant="headingMd">
                    Bottom {getLimits(store.tier as any)?.worstProductsLimit ?? 10} Products
                  </Text>
                  <DataTable
                    columnContentTypes={['text', 'numeric', 'text', 'text']}
                    headings={['Product', 'Score', 'Tier', 'Store']}
                    rows={worstRows}
                  />
                  <InlineStack align="end">
                    <Button onClick={() => navigate('/products')}>
                      View All Products
                    </Button>
                  </InlineStack>
                </BlockStack>
              </Card>
            )}
          </BlockStack>
        </Layout.Section>


      </Layout>

      </BlockStack>

    </Page>
    </PageEntrance>
  );
}
